import CloseIcon from '@mui/icons-material/Close';
import { Tooltip } from '@mui/material';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useEffect, useRef, useState, FC, ChangeEvent, useCallback, useMemo, memo } from 'react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';
import Switch from '../../components/core/Switch/Switch';
import Table from '../../components/Table/Table';
import {
	useGetOrganizationNotificationsSettingsQuery,
	useUpdateOrganizationNotificationsSettingsMutation
} from '../../services/endpoints/notificationsEndpoints';
import type { OrganizationNotificationRow } from '../../services/endpoints/notificationsEndpoints';
import { getLocalAuth } from '../../utils/environmentUtils';
import BasicCellText from './BasicCellText';
import { useAppSelector } from '../../redux/hooks';
import { GridRowIdGetter } from '@mui/x-data-grid/models/gridRows';
import usePermissions from '../../hooks/usePermissions';
import { ALLOWED_ACCOUNT_TYPES_FOR_ORG_NOTIFICATION_SETTINGS } from '../../constants/common';

interface AlertState {
	isOpen: boolean;
	message?: string;
	status?: 'success' | 'error' | 'warning' | 'info';
}

interface CellValues {
	id: string | number;
	row: OrganizationNotificationRow;
	formattedValue?: string;
}

const Organization: FC = () => {
	const accountSource = useAppSelector((state) => state?.login?.accountSource);
	const apiToken = getLocalAuth(accountSource)?.access_token;
	const { userAccountTypeID } = usePermissions();

	const canModifySettings =
		ALLOWED_ACCOUNT_TYPES_FOR_ORG_NOTIFICATION_SETTINGS.includes(userAccountTypeID);

	const [updateOrganizationNotificationsSettings] =
		useUpdateOrganizationNotificationsSettingsMutation();

	const { data, isLoading, error } = useGetOrganizationNotificationsSettingsQuery(
		{
			apiToken
		},
		{ refetchOnMountOrArgChange: true }
	);

	const [rows, setRows] = useState<OrganizationNotificationRow[]>([]);
	const [rowCount, setRowCount] = useState<number>(0);
	const [alertBalloon, setAlertBalloon] = useState<AlertState>({ isOpen: false });
	const [infoBalloon, setInfoBalloon] = useState<AlertState>({ isOpen: true });

	const handleInfoClose = useCallback(() => setInfoBalloon({ isOpen: false }), []);

	useEffect(() => {
		if (data) {
			setRows(data.results);
			setRowCount(data.count);
		}
	}, [data]);

	const isSettingUpdatingRef = useRef<Record<string | number, boolean>>({});

	const handleAlert = useCallback((message: string, status: AlertState['status']) => {
		setAlertBalloon({ isOpen: true, message, status });
		const timer = setTimeout(() => {
			setAlertBalloon({ isOpen: false, status });
		}, 10000);
		return () => clearTimeout(timer);
	}, []);

	const getMessage = useCallback(
		(response: OrganizationNotificationRow, isEnabled: boolean): string => {
			if (response?.email_template?.email_template_name) {
				return `${response.email_template.email_template_name} notifications ${
					isEnabled ? 'enabled' : 'disabled'
				}.`;
			}
			return `Notification ${isEnabled ? 'enabled' : 'disabled'}.`;
		},
		[]
	);

	const handleChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>, settingId: string | number) => {
			const newSettingValue = event.target.checked;

			// Store the previous value to revert if the update fails
			const previousValue = rows.find(
				(row) => row.organization_notification_settings_id === settingId
			)?.active;

			// Optimistically update the UI
			setRows((prevRows) =>
				prevRows.map((row) =>
					row.organization_notification_settings_id === settingId
						? { ...row, active: newSettingValue }
						: row
				)
			);

			if (isSettingUpdatingRef.current[settingId]) {
				return;
			}
			isSettingUpdatingRef.current[settingId] = true;

			updateOrganizationNotificationsSettings({
				id: String(settingId),
				enabled: newSettingValue,
				apiToken: apiToken
			})
				.then(
					(
						result:
							| { data?: OrganizationNotificationRow }
							| { error: FetchBaseQueryError | SerializedError }
					) => {
						if ('error' in result) {
							// Revert the UI to the previous value
							setRows((prevRows) =>
								prevRows.map((row) =>
									row.organization_notification_settings_id === settingId
										? { ...row, active: previousValue ?? false }
										: row
								)
							);
							handleAlert('Error updating setting', 'error');
						} else if ('data' in result && result.data) {
							const message = getMessage(result?.data, newSettingValue);
							message?.length && handleAlert(message, 'success');
						}
						isSettingUpdatingRef.current[settingId] = false;
					}
				)
				.catch((error: Error) => {
					// Revert the UI to the previous value
					setRows((prevRows) =>
						prevRows.map((row) =>
							row.organization_notification_settings_id === settingId
								? { ...row, active: previousValue ?? false }
								: row
						)
					);
					console.error('Error updating setting:', error);
					handleAlert('Error updating setting', 'error');
					isSettingUpdatingRef.current[settingId] = false;
				});
		},
		[updateOrganizationNotificationsSettings, apiToken, getMessage, handleAlert, rows]
	);

	const columns = useMemo(
		() => [
			{
				field: 'email_template_name',
				headerName: 'Email Notification',
				description: 'Email notification to be sent',
				flex: 1,
				minWidth: 350,
				renderCell: (cellValues: CellValues) => {
					const emailTemplateName = cellValues?.row?.email_template?.email_template_name;
					return (
						<BasicCellText
							cellValues={{ ...cellValues, formattedValue: emailTemplateName }}
							isDate={false}
							disabled={!canModifySettings}
						/>
					);
				}
			},
			{
				field: 'description',
				headerName: 'Description',
				description: 'Description of the email notification',
				flex: 2,
				minWidth: 300,
				renderCell: (cellValues: CellValues) => {
					const description = cellValues?.row?.email_template?.description;
					return (
						<BasicCellText
							cellValues={{ ...cellValues, formattedValue: description }}
							isDate={false}
							disabled={!canModifySettings}
						/>
					);
				}
			},
			{
				field: 'active',
				headerName: 'Notification Enabled',
				description:
					'Indicates if the email notification is sent across all programs in the organization',
				flex: 1,
				minWidth: 120,
				renderCell: (cellValues: CellValues) => {
					const settingId = cellValues?.id;
					const tooltipTitle = !canModifySettings
						? 'Please contact your organizations master account holder to make any changes to this setting'
						: '';
					return (
						<Tooltip title={tooltipTitle} arrow placement="top">
							<div>
								<Switch
									id={`enabled-toggle-${cellValues?.id}`}
									disabled={!canModifySettings || isSettingUpdatingRef.current[settingId]}
									onChange={(event: ChangeEvent<HTMLInputElement>) => {
										handleChange(event, settingId);
									}}
									checked={!!cellValues?.formattedValue}
									size="medium"
								/>
							</div>
						</Tooltip>
					);
				}
			}
		],
		[handleChange, canModifySettings]
	);

	const getRowId = useCallback<GridRowIdGetter<OrganizationNotificationRow>>(
		(cell) => cell?.organization_notification_settings_id,
		[]
	);

	const closeAlert = useCallback(() => {
		setAlertBalloon({ isOpen: false });
	}, []);

	const getErrorMessage = useCallback(() => {
		if (error && 'status' in error) {
			const fetchError = error as FetchBaseQueryError;
			if (fetchError.status === 403) {
				return 'Unable to view these notification settings due to current permissions';
			}
		}
		return 'Unable to retrieve notification preferences';
	}, [error]);

	return (
		<div>
			<h4>Organization Email Notification Preferences</h4>

			<Collapse in={infoBalloon.isOpen}>
				<Stack sx={{ width: '100%', marginTop: '20px' }} spacing={2}>
					<Alert
						variant="outlined"
						severity="info"
						action={
							<IconButton aria-label="close" color="inherit" size="small" onClick={handleInfoClose}>
								<CloseIcon fontSize="inherit" />
							</IconButton>
						}
					>
						Disabling a notification will override that notification if enabled at the program level
					</Alert>
				</Stack>
			</Collapse>

			<Collapse in={alertBalloon.isOpen}>
				<Alert
					severity={alertBalloon?.status}
					action={
						<IconButton
							aria-label="close"
							color={alertBalloon?.status}
							size="small"
							onClick={closeAlert}
						>
							<CloseIcon fontSize="inherit" />
						</IconButton>
					}
					sx={{ mb: 2, mt: 2 }}
				>
					{alertBalloon?.message}
				</Alert>
			</Collapse>

			{error && (
				<Stack sx={{ width: '100%', marginTop: '20px' }} spacing={2}>
					<Alert variant="outlined" severity="error">
						{getErrorMessage()}
					</Alert>
				</Stack>
			)}

			{!error && !isLoading && rowCount === 0 && (
				<Stack sx={{ width: '100%', marginTop: '20px' }} spacing={2}>
					<Alert variant="outlined" severity="info">
						Unable to retrieve notification preferences
					</Alert>
				</Stack>
			)}

			{!error && rows && rowCount > 0 && (
				<Table
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore we need to convert Table to TS to fix this ignore
					getRowId={getRowId}
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore we need to convert Table to TS to fix this ignore
					columns={columns}
					rowHeight={true}
					enableAutoPageSize
					rows={rows}
					rowCount={rowCount}
					loading={isLoading}
					pagination={false}
					hideFilters
					hideTableActions
				/>
			)}
		</div>
	);
};

const MemoizedOrganization = memo(Organization);
MemoizedOrganization.displayName = 'Organization';

export default MemoizedOrganization;
